// useVoiceRecorder — ports voice.js (recording + VAD + upload).
// Records via MediaRecorder, runs the frequency-band VAD for the volume meter
// and a no-speech discard, caps at 120s, and uploads to /voice on stop.
// Returns recording state + a toggle(). onPaywall fires on a 402.

import { useCallback, useEffect, useRef, useState } from 'react'
import { VoiceRecordingVAD } from '../lib/voiceVad'
import { voiceApi } from '../api/voice'
import { ApiError } from '../api/client'
import { useMessagingStore } from '../store/messagingStore'
import { toast } from '../store/toastStore'
import * as metrica from '../lib/metrica'

const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4']
const MAX_SECONDS = 120

function pickMime() {
  for (const t of MIME_TYPES) if (MediaRecorder.isTypeSupported(t)) return t
  return ''
}

function extFor(mime: string) {
  if (mime.includes('webm')) return 'webm'
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('mp4')) return 'm4a'
  return 'webm'
}

interface UseVoiceRecorderOptions {
  onPaywall?: () => void
}

// Pull a human-readable reason out of an ApiError's structured `detail`
// (string | {reason} | validation array), falling back to the Error message.
function uploadErrorMessage(err: unknown): string {
  const fallback = 'Не удалось отправить голосовое сообщение'
  if (err instanceof ApiError) {
    const detail = err.body?.detail
    if (typeof detail === 'string') return detail
    if (detail && !Array.isArray(detail) && detail.reason) return detail.reason
  }
  return (err instanceof Error && err.message) || fallback
}

export function useVoiceRecorder({ onPaywall }: UseVoiceRecorderOptions = {}) {
  const [isRecording, setIsRecording] = useState(false)
  const [volume, setVolume] = useState(0)
  const [speaking, setSpeaking] = useState(false)
  const [elapsed, setElapsed] = useState(0)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const vadRef = useRef<VoiceRecordingVAD | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const lastVolRef = useRef(0)

  const addMessage = useMessagingStore((s) => s.addMessage)
  const setLoading = useMessagingStore((s) => s.setLoading)
  const setTyping = useMessagingStore((s) => s.setTyping)

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = null
    vadRef.current?.stop()
    vadRef.current = null
    streamRef.current = null
    setVolume(0)
    setSpeaking(false)
    setElapsed(0)
  }

  // Hard teardown if the component unmounts mid-recording (e.g. the user
  // navigates to another section while recording). Without this the mic tracks
  // stay live, the AudioContext stays open and the timer keeps ticking — the
  // old singleton app never unmounted, so it never hit this.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      const rec = recorderRef.current
      if (rec && rec.state !== 'inactive') {
        rec.onstop = null // skip the upload/setState path on an unmounted component
        rec.ondataavailable = null
        try {
          rec.stop()
        } catch {
          /* already stopped */
        }
      }
      streamRef.current?.getTracks().forEach((t) => t.stop())
      vadRef.current?.stop()
    }
  }, [])

  const upload = useCallback(async () => {
    if (!chunksRef.current.length) {
      toast('Аудио не записано', 'error') // original showed a "No audio recorded" toast
      return
    }
    const mime = recorderRef.current?.mimeType || 'audio/webm'
    const blob = new Blob(chunksRef.current, { type: mime })
    chunksRef.current = []

    setTyping(true)
    setLoading(true)
    try {
      const data = await voiceApi.upload(blob, extFor(mime))
      setTyping(false)
      metrica.reachGoal('voice_sent')
      addMessage('user', `🎤 ${data.transcription || '[Voice message]'}`)
      for (const msg of data.messages || []) {
        addMessage('assistant', msg.content, {
          contentType: msg.content_type || 'text',
          mediaUrl: msg.media_url || null,
          metadata: msg.metadata,
        })
      }
    } catch (err) {
      setTyping(false)
      if (err instanceof ApiError && err.status === 402) {
        onPaywall?.()
      } else {
        toast(uploadErrorMessage(err), 'error')
      }
    } finally {
      setLoading(false)
    }
  }, [addMessage, setLoading, setTyping, onPaywall])

  const start = useCallback(async () => {
    if (!navigator.mediaDevices) {
      toast('Браузер не поддерживает запись голоса', 'error')
      return
    }
    if (!window.isSecureContext) {
      toast('Микрофон требует HTTPS или localhost', 'error')
      return
    }
    const mime = pickMime()
    if (!mime) {
      toast('Запись голоса не поддерживается в этом браузере', 'error')
      return
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      const e = err as DOMException
      if (e.name === 'NotAllowedError') toast('Доступ к микрофону запрещён', 'error')
      else if (e.name === 'NotFoundError') toast('Микрофон не найден', 'error')
      else toast('Не удалось начать запись: ' + e.message, 'error')
      return
    }

    streamRef.current = stream
    const vad = new VoiceRecordingVAD({ threshold: 45, hangoverTime: 1000 })
    // Throttle the meter to ~15fps: the VAD calls this every rAF (~60fps) and
    // each setVolume re-renders the whole Chat. The original wrote bar.style.width
    // directly (free); throttling keeps the meter smooth without 60fps re-renders.
    vad.onVolumeUpdate = (v) => {
      const now = Date.now()
      if (now - lastVolRef.current >= 66) {
        lastVolRef.current = now
        setVolume(v)
      }
    }
    vad.onSpeechState = setSpeaking
    await vad.start(stream)
    vadRef.current = vad

    const recorder = new MediaRecorder(stream, { mimeType: mime })
    recorderRef.current = recorder
    chunksRef.current = []

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop())
      const hadVoice = vadRef.current?.isVoiceDetected
      cleanup()
      if (!hadVoice) {
        chunksRef.current = []
        toast('Речь не обнаружена. Подойдите ближе к микрофону и попробуйте снова.', 'warning')
        metrica.reachGoal('voice_discarded', { reason: 'no_voice' })
        return
      }
      upload()
    }
    recorder.onerror = (e) => {
      const msg = (e as unknown as { error?: { message?: string } }).error?.message || ''
      toast('Ошибка записи: ' + msg, 'error')
      stopRecording()
    }

    recorder.start(1000)
    setIsRecording(true)
    const startedAt = Date.now()
    timerRef.current = setInterval(() => {
      const s = Math.floor((Date.now() - startedAt) / 1000)
      setElapsed(s)
      if (s >= MAX_SECONDS) stopRecording()
    }, 1000)
    // stopRecording is a stable useCallback([]) defined below; including it
    // would just recreate `start` on every render for no behavioral gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upload])

  const stopRecording = useCallback(() => {
    setIsRecording(false)
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    // onstop handles VAD discard / upload + cleanup
  }, [])

  const toggle = useCallback(() => {
    if (isRecording) stopRecording()
    else start()
  }, [isRecording, start, stopRecording])

  return { isRecording, volume, speaking, elapsed, toggle }
}
